import React, { useCallback, useEffect } from 'react'

import { useLocation } from 'react-router-dom'

import { useAttributeOptionActions } from 'redux/actions/AttributeOptions'
import { useEntityActions } from 'redux/actions/Entities'

import { DashboardVariantType, isPublicDashboard } from 'types/GlobalDashboard'
import { TCoreWidget } from 'types/GlobalWidget'
import { useDashboard, useDashboardFetchStatus } from 'redux/hooks/Dashboards'
import { useAttributeOptionsFetched } from 'redux/hooks/AttributeOptions'

import { useDashboardWidgetActions } from 'redux/actions/Widgets/DashboardWidgets'

import { useTranslation } from 'language/translator'
import { useWidgetLoadingTitles } from 'utils/loadingTitles'

import Box from 'components_new/atoms/Box'

import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'
import LoadingSection from 'components_new/molecules/LoadingSection'

import Dashboard from 'components_new/organisms/Dashboard'

import PublicDashboardTemplate from 'components_new/templates/PublicDashboardTemplate'

import { parseQueryParamToNumber } from 'utils/queryHandler'

import { useParams } from 'utils/hooks'
import { useDashboardActions } from 'redux/actions/Dashboards'

const ApiAccessDashboardPage = () => {
  const attributeOptionsFetched = useAttributeOptionsFetched()

  const { tryGetAllAttributeOptions } = useAttributeOptionActions()
  const { tryGetAllEntities } = useEntityActions()

  const { t } = useTranslation()
  const loadingTitles = useWidgetLoadingTitles()

  const params = useParams<{ id: string }>()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)

  const allowDashboardFilter = queryParams.get('filter') === 'true'
  const allowExport = queryParams.get('export') === 'true'
  const mobileBreakpoint = parseQueryParamToNumber(
    queryParams.get('mobile_breakpoint')
  )
  const mobileResponsive = queryParams.get('responsive_mode') === 'mobile'

  const token = queryParams.get('token')
  const companies = queryParams.get('companies')?.split(',') || []

  const dashboard = useDashboard(params.id)
  const dashboardFetchStatus = useDashboardFetchStatus(params.id)

  const { tryGetOneApiAccessDashboardWidget } = useDashboardWidgetActions()
  const { tryGetOneApiAccessDashboard } = useDashboardActions()

  // Pre-configured fetch function for individual widgets
  const fetchWidgetData = useCallback(
    (widget: TCoreWidget) => {
      if (!token) return

      tryGetOneApiAccessDashboardWidget(widget, token, params.id, companies, '')
    },
    [token, companies, params.id]
  )

  useEffect(() => {
    tryGetOneApiAccessDashboard(params.id)
    tryGetAllEntities()

    if (!attributeOptionsFetched) {
      tryGetAllAttributeOptions()
    }
  }, [])

  if (!dashboard || !isPublicDashboard(dashboard)) {
    // check for loading or error
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100vw',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {dashboardFetchStatus.status === 'error' ? (
          <ErrorPlaceholder
            code={401}
            body={t('dashboards', 'error_public_dashboard_body')}
          />
        ) : (
          <LoadingSection description={loadingTitles} />
        )}
      </Box>
    )
  }

  return (
    <PublicDashboardTemplate
      dashboard={dashboard}
      allowDashboardFilter={allowDashboardFilter}
      allowExport={allowExport}
      mobileBreakpoint={mobileBreakpoint}
      mobileResponsive={mobileResponsive}
    >
      <Dashboard
        customization={dashboard.white_label_settings}
        dashboard={dashboard}
        dashboardFilters={dashboard.dashboard_filters}
        fetchWidgetData={fetchWidgetData}
        variant={{
          embedded: true,
          type: DashboardVariantType.API
        }}
      />
    </PublicDashboardTemplate>
  )
}

export default ApiAccessDashboardPage
